<script lang="ts">
export default {
  name: 'AnalyticsList',
};

interface Target {
  card: Card | null;
  action: 'edit' | 'delete' | 'publish' | 'unpublish' | null;
  type: 'tableau' | 'document' | 'volatility' | 'stock_drop' | 'cyber' | null;
  modal: {
    title: string;
    open: boolean;
    actionLabel: string;
    message?: string;
  };
}
</script>

<script setup lang="ts">
import {
  ExternalLinkIcon,
  Trash2Icon,
  XIcon,
  EditIcon,
  ChartNoAxesCombinedIcon,
  FileIcon,
  LayoutTemplateIcon,
  UsersIcon,
  PinIcon,
  AlertTriangleIcon,
} from '@lucide/vue';
import FavoriteIndicator from '@/components/FavoriteIndicator.vue';
import { useFavorites } from '@/composables/useFavorites';
import AppButton from '@/components/v2/AppButton.vue';
import AppModal from '@/components/v2/AppModal.vue';
import TableauModal from '@/components/TableauModal.vue';
import { TableauDashboard, TableauType } from '@/types/TableauDashboard';
import { tableauService, volatilityModelService, stockDropModelService, cyberCubeService } from '@/services/api';
import { computed, inject, ref, reactive, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { VolatilityModel } from '@/types/VolatilityModel';
import { filters } from '@/utilities/Filters';
import { AppState } from '@/store';
import { Store } from 'vuex';
import Badge from '@/components/Badge.vue';
import AnalyticsUploadModal from '@/components/AnalyticsUploadModal.vue';
import DocumentEditModal from '@/components/v2/DocumentEditModal.vue';
import DocumentDeleteConfirmModal from '@/components/v2/DocumentDeleteConfirmModal.vue';
import { Document, UpdateDocumentRequest, DocumentType } from '@/types/v2/Document';
import {
  Card,
  DocumentCard,
  isDocumentCard,
  isTableauDashboardCard,
  isVolatilityModelCard,
  isStockDropModelCard,
  isCyberModelCard,
  isPinned,
  isPinnable,
  TableauDashboardCard,
  VolatilityModelCard,
  StockDropModelCard,
  CyberModelCard,
} from '@/types/analytics/Analytics';
import { documentService } from '@/services/v2/DocumentService';
import { StockDropModel } from '@/types/StockDropModel';
import { Category, hasAnyUploadOptions } from '@/utilities/Analytics';
import { IsUserAClient, UserHasPermission } from '@/utilities/AuthUtils';
import { orgName } from '@/utilities/OrgConfig';
import { UserPermission } from '@/types/UserPermissions';

// ---------
// constants
// ---------

const ADVISORY_PUBLISH_MESSAGE = 'This resource will be published and made available to all clients with access to the analytics tab';
const ACCOUNT_ANALYTICS_DOC_PUBLISH_MESSAGE = 'Clients with access to this account will be notified';

// ------
// emits
// ------

const emit = defineEmits(['updated']);

// -----
// props
// -----

const props = defineProps<{
  loading: boolean;
  selectedCategoryType: number;
  selectedCategoryName: string;
  dashboards: TableauDashboard[];
  documents: Document[];
  documentTypes: DocumentType[];
  tableauTypes: TableauType[];
  volatilityModels?: VolatilityModel[];
  stockDropModels?: StockDropModel[];
  cyberModels?: CyberModelCard[];
  accountId?: number;
}>();

// -----
// data
// -----

const tableauDomain = import.meta.env.VITE_TABLEAU_BASE_DOMAIN;
const openAnalyticsModal = ref<boolean>(false);
const showDocumentEditModal = ref<boolean>(false);
const target = reactive<Target>({
  card: null,
  action: null,
  type: null,
  modal: {
    title: '',
    open: false,
    actionLabel: '',
    message: undefined,
  },
});
const documentDeleteModal = ref<{ show: boolean; documentId: string | null; fileName: string | null }>({
  show: false,
  documentId: null,
  fileName: null,
});
const searchFilter = ref<string>('');
const selectedDashboard = ref<TableauDashboard | null>(null);
const store = inject<Store<AppState>>('store');

const router = useRouter();
const route = useRoute();

const { isFavorited, toggleFavorite } = useFavorites();
const isFavoritesCategory = inject<import('vue').ComputedRef<boolean>>('isFavoritesCategory');

function cardServiceName(card: Card): string {
  if (isTableauDashboardCard(card)) return 'analytics-tableau';
  if (isDocumentCard(card)) return 'analytics-document';
  if (isVolatilityModelCard(card)) return 'analytics-volatility';
  if (isStockDropModelCard(card)) return 'analytics-stockdrop';
  if (isCyberModelCard(card)) return 'analytics-cyber';
  return 'analytics-unknown';
}

async function handleToggleFavorite(card: Card) {
  await toggleFavorite(cardServiceName(card), card.id);
}

// ----------
// functions
// ----------

function handleClearSearchFilter() {
  searchFilter.value = '';
}

function handleOpenUploadModal() {
  openAnalyticsModal.value = true;
}

function openAnalyticsMailto() {
  window.location.href = 'mailto:analytics@cacgroup.com';
}

function openDocumentEditModal() {
  showDocumentEditModal.value = true;
}

function closeDocumentEditModal() {
  clearTarget();
  showDocumentEditModal.value = false;
}

async function handleUploadDashboard() {
  emit('updated');
}

function openEditModal(card: Card) {
  if (isTableauDashboardCard(card) || isDocumentCard(card)) {
    target.card = card;
    target.action = 'edit';
    target.modal.open = true;
    target.type = isTableauDashboardCard(card) ? 'tableau' : 'document';
  }
  if (isDocumentCard(card)) {
    openDocumentEditModal();
  }
}

function routeToEdit(card: Card) {
  if (isStockDropModelCard(card)) {
    if (props.accountId) {
      router.push(`/account/${card.model.accountId}/analytics/category/${props.selectedCategoryType}/stockdrop/${card.model.id}/edit`);
    } else {
      router.push(`/analytics/category/${props.selectedCategoryType}/stockdrop/${card.model.id}/edit`);
    }
  } else if (isVolatilityModelCard(card)) {
    if (props.accountId) {
      router.push(`/account/${card.model.accountId}/analytics/category/${props.selectedCategoryType}/volatility/${card.model.id}/edit`);
    } else {
      router.push(`/analytics/category/${props.selectedCategoryType}/volatility/${card.model.id}/edit`);
    }
  } else if (isCyberModelCard(card)) {
    if (props.accountId) {
      router.push(`/account/${card.accountId}/analytics/category/${props.selectedCategoryType}/cyber/edit`);
    } else {
      router.push(`/analytics/category/${props.selectedCategoryType}/cyber/${card.accountId}/edit`);
    }
  }
}

async function handleTogglePin(card: Card) {
  if (isTableauDashboardCard(card)) {
    await tableauService.togglePinned(card.dashboard?.id);
  }
  if (isDocumentCard(card)) {
    await documentService.togglePinned(card.document?.documentId);
  }
  if (isVolatilityModelCard(card)) {
    await volatilityModelService.togglePinned(card.model?.id);
  }
  if (isStockDropModelCard(card)) {
    await stockDropModelService.togglePinned(card.model?.id);
  }
  emit('updated');
}

function handlePublishCyberCubeModel(card: CyberModelCard) {
  target.card = card;
  target.type = 'cyber';
  target.action = card.published ? 'unpublish' : 'publish';
  target.modal.title = `Confirm ${card.published ? 'Unpublishing' : 'Publishing'} CyberCube Model`;
  target.modal.actionLabel = card.published ? 'Unpublish' : 'Publish';
  target.modal.open = true;
}

function handlePublishDocument(card: DocumentCard) {
  target.card = card;
  target.type = 'document';
  target.action = card.document.published ? 'unpublish' : 'publish';
  target.modal.title = `Confirm ${card.document.published ? 'Unpublishing' : 'Publishing'} Document`;
  target.modal.actionLabel = card.document.published ? 'Unpublish' : 'Publish';

  // Add special message for Advisory resources when publishing
  if (props.selectedCategoryName === Category.Advisory && !card.document.published) {
    target.modal.message = ADVISORY_PUBLISH_MESSAGE;
  } else if (!card.document.published) {
    target.modal.message = ACCOUNT_ANALYTICS_DOC_PUBLISH_MESSAGE;
  } else {
    target.modal.message = undefined;
  }

  target.modal.open = true;
}

function handlePublishDashboard(card: TableauDashboardCard) {
  target.card = card;
  target.type = 'tableau';
  target.action = card.dashboard.published ? 'unpublish' : 'publish';
  target.modal.title = `Confirm ${card.dashboard.published ? 'Unpublishing' : 'Publishing'} Tableau Dashboard`;
  target.modal.actionLabel = card.dashboard.published ? 'Unpublish' : 'Publish';

  // Add special message for Advisory resources when publishing
  if (props.selectedCategoryName === Category.Advisory && !card.dashboard.published) {
    target.modal.message = ADVISORY_PUBLISH_MESSAGE;
  } else {
    target.modal.message = undefined;
  }

  target.modal.open = true;
}

function handleDelete(card: Card) {
  if (isTableauDashboardCard(card)) {
    target.card = card;
    target.type = 'tableau';
    target.action = 'delete';
    target.modal.title = 'Confirm Delete Dashboard';
    target.modal.actionLabel = 'Delete';
    target.modal.open = true;
  } else if (isDocumentCard(card)) {
    documentDeleteModal.value = {
      show: true,
      documentId: card.document.documentId,
      fileName: card.document.displayName || card.document.fileName,
    };
  } else if (isStockDropModelCard(card)) {
    target.card = card;
    target.type = 'stock_drop';
    target.action = 'delete';
    target.modal.title = 'Confirm Delete Stock Drop Model';
    target.modal.actionLabel = 'Delete';
    target.modal.open = true;
  } else if (isVolatilityModelCard(card)) {
    target.card = card;
    target.type = 'volatility';
    target.action = 'delete';
    target.modal.title = 'Confirm Delete Volatility Model';
    target.modal.actionLabel = 'Delete';
    target.modal.open = true;
  }
}

function handleDocumentDeleteClose() {
  documentDeleteModal.value = {
    show: false,
    documentId: null,
    fileName: null,
  };
}

function handleDocumentDeleted() {
  handleDocumentDeleteClose();
  clearTarget();
  emit('updated');
}

async function handleModalConfirm(request?: UpdateDocumentRequest) {
  if (target.card && isTableauDashboardCard(target.card)) {
    if (target.action === 'delete') {
      if (props.accountId) {
        await tableauService.deleteDashboard(target.card.dashboard.accountId, target.card.dashboard.id);
      } else {
        await tableauService.deleteDashboardV2(target.card.dashboard.id);
      }
      clearTarget();
      emit('updated');
    } else if (target.action === 'publish' || target.action === 'unpublish') {
      await tableauService.togglePublished(target.card.dashboard.id);
      clearTarget();
      emit('updated');
    }
  } else if (target.card && isDocumentCard(target.card)) {
    if (target.action === 'publish') {
      await documentService.publishDocument(target.card.document.documentId);
      clearTarget();
      emit('updated');
    } else if (target.action === 'unpublish') {
      await documentService.unPublishDocument(target.card.document.documentId);
      clearTarget();
      emit('updated');
    } else if (target.action === 'edit' && request) {
      await documentService.updateDocument(target.card.document.documentId, request);
      closeDocumentEditModal();
      emit('updated');
    }
  } else if (target.card && isStockDropModelCard(target.card)) {
    if (target.action === 'delete') {
      await stockDropModelService.deleteStockDropModel(target.card.model.id);
      clearTarget();
      emit('updated');
    }
  } else if (target.card && isVolatilityModelCard(target.card)) {
    if (target.action === 'delete') {
      await volatilityModelService.deleteVolatilityModel(target.card.model.id);
      clearTarget();
      emit('updated');
    }
  } else if (target.card && isCyberModelCard(target.card)) {
    if (target.action === 'publish') {
      await cyberCubeService.publishCyberAnalysisRun(Number(target.card.id));
      clearTarget();
      emit('updated');
    } else if (target.action === 'unpublish') {
      await cyberCubeService.unpublishCyberAnalysisRun(Number(target.card.id));
      clearTarget();
      emit('updated');
    }
  }
}

function clearTarget() {
  target.card = null;
  target.type = null;
  target.action = null;
  target.modal.title = '';
  target.modal.actionLabel = '';
  target.modal.message = undefined;
  target.modal.open = false;
}

async function handleModalCancel() {
  clearTarget();
}

async function handleSelect(card: Card) {
  const categoryType = isFavoritesCategory?.value
    ? isTableauDashboardCard(card)
      ? card.dashboard.analyticsTypeId
      : isVolatilityModelCard(card) || isStockDropModelCard(card)
        ? props.selectedCategoryType
        : props.selectedCategoryType
    : props.selectedCategoryType;

  if (isTableauDashboardCard(card)) {
    selectedDashboard.value = card.dashboard;
    if (props.accountId) {
      router.push(`/account/${card.dashboard.accountId}/analytics/category/${categoryType}/dashboard/${card.dashboard.id}`);
    } else {
      router.push(`/analytics/category/${categoryType}/dashboard/${card.dashboard.id}`);
    }
  } else if (isVolatilityModelCard(card)) {
    if (props.accountId) {
      router.push(`/account/${card.model.accountId}/analytics/category/${categoryType}/volatility/${card.model.id}`);
    } else {
      router.push(`/analytics/category/${categoryType}/volatility/${card.model.id}`);
    }
  } else if (isStockDropModelCard(card)) {
    if (props.accountId) {
      router.push(`/account/${card.model.accountId}/analytics/category/${categoryType}/stockdrop/${card.model.id}`);
    } else {
      router.push(`/analytics/category/${categoryType}/stockdrop/${card.model.id}`);
    }
  } else if (isDocumentCard(card)) {
    const target = await documentService.getDocumentFileById(card.document.documentId);
    const anchorElement = document.createElement('a');
    anchorElement.href = target;
    anchorElement.download = card.document.displayName || 'download';
    anchorElement.setAttribute('target', '_blank');
    anchorElement.setAttribute('rel', 'noopener noreferrer');
    anchorElement.click();
    anchorElement.remove();
  } else if (isCyberModelCard(card)) {
    if (props.accountId) {
      router.push(`/account/${card.accountId}/analytics/category/${categoryType}/cyber/view`);
    } else {
      router.push(`/analytics/category/${categoryType}/cyber/${card.accountId}/view`);
    }
  }
}
function showCyberCubeActions(card: Card) {
  return hasAnalyticsWritePermission.value && isCyberModelCard(card);
}

function showTableauActions(card: Card) {
  return hasAnalyticsWritePermission.value && isTableauDashboardCard(card);
}

function showDocumentActions(card: Card) {
  return hasAnalyticsWritePermission.value && isDocumentCard(card);
}

function showModelEdit(card: Card) {
  if (isVolatilityModelCard(card)) {
    return !isAClientAccount.value || UserHasPermission(store?.state.user, UserPermission.AnalyticsRead);
  }
  return !isAClientAccount.value && (isStockDropModelCard(card) || isCyberModelCard(card));
}

function showModelDelete(card: Card) {
  if (isStockDropModelCard(card)) {
    return hasAnalyticsWritePermission.value || store?.state.user?.email === card.model.createdBy;
  } else if (isVolatilityModelCard(card)) {
    return hasAnalyticsWritePermission.value || store?.state.user?.email === card.model.insertedBy;
  } else {
    return false;
  }
}

function showPin(card: Card) {
  return (hasAnalyticsWritePermission.value && isPinnable(card)) || isPinned(card);
}

function showDelete(card: Card) {
  return (tableauDomainExists.value && showTableauActions(card)) || showDocumentActions(card) || showModelDelete(card);
}

// ---------
// computed
// ---------

const isAClientAccount = computed(() => {
  return IsUserAClient(store?.state.user);
});

const isClientViewEnabled = computed(() => {
  return store?.state.clientViewState.isClientViewEnabled;
});

const isModelOrFormView = computed(() => {
  return (
    route.params.dashboardId || route.params.volatilityId || route.params.stockdropId || route.path.includes('/new') || route.path.includes('/cyber')
  );
});

const hasAnalyticsWritePermission = computed<boolean>(() => {
  const roles = store?.state?.user?.roles || [];
  const permissions = store?.state?.user?.permissions || [];
  return roles.includes('admin') || permissions.includes('analytics:write');
});

const isUploadButtonVisible = computed<boolean>(() => {
  if (store?.state?.user) {
    return hasAnyUploadOptions(store?.state?.user, props.selectedCategoryName as Category);
  }

  return false;
});

const filteredVolatilityModels = computed<VolatilityModel[]>(() => {
  if (props.volatilityModels && props.volatilityModels.length > 0) {
    return props.volatilityModels.filter((model) => {
      return (
        model.name?.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
        model.accountName?.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
        model.insertedBy?.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
        model.updatedBy?.toLowerCase().includes(searchFilter.value.toLowerCase())
      );
    });
  }
  return [];
});

const filteredStockDropModels = computed<StockDropModel[]>(() => {
  if (props.stockDropModels && props.stockDropModels.length > 0) {
    return props.stockDropModels.filter((model) => {
      return (
        model.name?.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
        model.accountName?.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
        model.createdBy?.toLowerCase().includes(searchFilter.value.toLowerCase())
      );
    });
  }
  return [];
});

const filteredCyberModels = computed<CyberModelCard[]>(() => {
  if (props.cyberModels && props.cyberModels.length > 0) {
    return props.cyberModels.filter((card) => {
      return (
        card.title?.toLowerCase().includes(searchFilter.value.toLowerCase()) || card.client?.toLowerCase().includes(searchFilter.value.toLowerCase())
      );
    });
  }
  return [];
});

const filteredDashboards = computed<TableauDashboard[]>(() => {
  return props.dashboards.filter((dashboard) => {
    return (
      dashboard.name.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
      dashboard.user.userEmail.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
      dashboard.dashboardUrl.toLowerCase().includes(searchFilter.value.toLowerCase()) ||
      dashboard.accountDerivedName?.toLowerCase().includes(searchFilter.value.toLowerCase())
    );
  });
});

const filteredDocuments = computed<Document[]>(() => {
  return props.documents.filter((document) => {
    const searchTerm = searchFilter.value.toLowerCase();

    // Check basic document properties
    const basicMatch =
      document.fileName.toLowerCase().includes(searchTerm) ||
      document.displayName?.toLowerCase().includes(searchTerm) ||
      document.documentTypeName?.toLowerCase().includes(searchTerm) ||
      document.createdByUser?.toLowerCase().includes(searchTerm);

    // Check document tags if they exist
    const tagMatch = document.tags?.some((tag) => tag.toLowerCase().includes(searchTerm)) || false;

    return basicMatch || tagMatch;
  });
});

const analyticsCards = computed<Card[]>(() => {
  const cards: Card[] = [];

  // add all the pinned items first
  filteredDashboards.value
    .filter((dashboard) => dashboard.pinned)
    .forEach((dashboard) => {
      cards.push({
        id: dashboard.id.toString(),
        type: 'tableau',
        url: dashboard.dashboardUrl,
        dashboard: dashboard,
        title: dashboard.name,
        client: dashboard.accountDerivedName || '--',
        created_by: dashboard.user.userEmail,
        updated_on: dashboard.updatedAt ? filters.date(dashboard.updatedAt) : '--',
      } as TableauDashboardCard);
    });

  filteredDocuments.value
    .filter((document) => document.pinned)
    .forEach((document) => {
      cards.push({
        id: document.documentId,
        type: 'document',
        document: document,
        title: document.displayName || document.fileName,
        client: document.accountName || '--',
        created_by: document.createdByUser,
        created_on: document.createdByDate,
        updated_on: document.updatedByDate ? filters.date(document.updatedByDate) : '--',
        pinned: document.pinned,
      } as DocumentCard);
    });

  filteredVolatilityModels.value
    .filter((model) => model.pinned)
    .forEach((model) => {
      cards.push({
        id: model.id?.toString() || '',
        type: 'volatility',
        model: model,
        title: model.name || '--',
        client: model.accountName || '--',
        created_by: model.insertedBy || '--',
        created_on: model.insertedDate || '',
        updated_on: model.updatedDate ? filters.date(model.updatedDate) : '--',
        pinned: model.pinned,
      } as VolatilityModelCard);
    });

  filteredStockDropModels.value
    .filter((model) => model.pinned)
    .forEach((model) => {
      cards.push({
        id: model.id?.toString() || '',
        type: 'stock_drop',
        model: model,
        title: model.name || '--',
        client: model.accountId && model.accountName ? model.accountName : '--',
        created_by: model.createdBy || '--',
        created_on: model.createdDate || '',
        updated_on: model.updatedDate ? filters.date(model.updatedDate) : '--',
        pinned: model.pinned,
      } as StockDropModelCard);
    });

  // sort pinned cards
  cards.sort((a, b) => new Date(b.updated_on).getTime() - new Date(a.updated_on).getTime());

  const nonPinnedCards: Card[] = [];

  // then add all non-pinned items
  filteredDashboards.value
    .filter((dashboard) => !dashboard.pinned)
    .forEach((dashboard) => {
      nonPinnedCards.push({
        id: dashboard.id.toString(),
        type: 'tableau',
        url: dashboard.dashboardUrl,
        dashboard: dashboard,
        title: dashboard.name,
        client: dashboard.accountDerivedName || '--',
        created_by: dashboard.user.userEmail,
        created_on: dashboard.createdDate,
        updated_on: dashboard.updatedAt ? filters.date(dashboard.updatedAt) : '--',
      } as TableauDashboardCard);
    });

  filteredDocuments.value
    .filter((document) => !document.pinned)
    .forEach((document) => {
      nonPinnedCards.push({
        id: document.documentId,
        type: 'document',
        document: document,
        title: document.displayName || document.fileName,
        client: document.accountName || '--',
        created_by: document.createdByUser,
        created_on: document.createdByDate || '',
        updated_on: document.updatedByDate ? filters.date(document.updatedByDate) : '--',
        pinned: document.pinned,
      } as DocumentCard);
    });

  filteredVolatilityModels.value
    .filter((model) => !model.pinned)
    .forEach((model) => {
      nonPinnedCards.push({
        id: model.id?.toString() || '',
        type: 'volatility',
        model: model,
        title: model.name || '--',
        client: model.accountName || '--',
        created_by: model.insertedBy || '--',
        created_on: model.insertedDate || '',
        updated_on: model.updatedDate ? filters.date(model.updatedDate) : '--',
        pinned: model.pinned,
      } as VolatilityModelCard);
    });

  filteredStockDropModels.value
    .filter((model) => !model.pinned)
    .forEach((model) => {
      nonPinnedCards.push({
        id: model.id?.toString() || '',
        type: 'stock_drop',
        model: model,
        title: model.name || '--',
        client: model.accountId && model.accountName ? model.accountName : '--',
        created_by: model.createdBy || '--',
        created_on: model.createdDate || '',
        updated_on: model.updatedDate ? filters.date(model.updatedDate) : '--',
        pinned: model.pinned,
      } as StockDropModelCard);
    });

  filteredCyberModels.value.forEach((card) => {
    nonPinnedCards.push({
      ...card,
      updated_on: filters.date(card.updated_on),
    });
  });

  nonPinnedCards.sort((a, b) => new Date(b.updated_on).getTime() - new Date(a.updated_on).getTime());
  cards.push(...nonPinnedCards);

  return cards;
});

const tableauDomainExists = computed<boolean>(() => !!tableauDomain);

// ---------
// watchers
// ---------

watch(
  () => props.selectedCategoryType,
  async (newVal, oldVal) => {
    if (newVal !== oldVal) {
      searchFilter.value = '';
    }
  },
);
</script>

<template>
  <div class="grow bg-white">
    <main
      v-if="!isModelOrFormView"
      ref="resourceTypeHeader">
      <header class="sticky top-16 mx-4 grid grid-cols-3 items-center border-b border-gray-300 bg-white p-4">
        <h1 class="text-2xl font-bold text-primary">{{ selectedCategoryName }}</h1>

        <label class="input mx-2 input-md flex items-center gap-2">
          <input
            type="text"
            placeholder="Search"
            class="grow"
            v-model="searchFilter" />
          <XIcon
            v-if="searchFilter.length > 0"
            :size="15"
            class="cursor-pointer"
            @click="handleClearSearchFilter" />
        </label>

        <div class="pointer-events-none flex justify-self-end">
          <AppButton
            primary
            class="pointer-events-auto mx-2 my-2 mr-2"
            @click="openAnalyticsMailto">
            Contact {{ orgName() }} Analytics
          </AppButton>
          <AppButton
            v-if="isUploadButtonVisible"
            primary
            :disabled="!tableauDomainExists"
            class="pointer-events-auto mx-2 my-2 mr-2"
            data-testid="analytics-upload-btn"
            @click="handleOpenUploadModal">
            Upload
          </AppButton>
        </div>
      </header>

      <div v-if="analyticsCards.length === 0 && !loading">
        <div class="mt-8 flex justify-center">
          <h2 class="text-2xl text-gray-500">No content found</h2>
        </div>
      </div>
      <div
        v-else
        class="flex w-full flex-wrap justify-start overflow-auto overflow-x-hidden p-4">
        <div class="flex flex-col flex-wrap content-start items-start gap-4 px-4 xl:flex-row">
          <div
            v-for="card in analyticsCards"
            :key="card.id"
            class="pointer-events-auto relative flex w-120 cursor-pointer flex-col justify-between gap-1 rounded-md border border-solid border-gray-300 p-5 shadow-md transition-all duration-150 hover:border-gray-400 hover:bg-gray-100 hover:shadow-none"
            :class="{
              'border-gray-400 bg-gray-100': isPinned(card),
              'max-h-36 min-h-36': isAClientAccount,
              'max-h-56 min-h-56': !isAClientAccount,
            }"
            @click="handleSelect(card)">
            <FavoriteIndicator
              v-if="!isAClientAccount"
              class="absolute top-2 right-2"
              :model-value="isFavorited(cardServiceName(card), card.id)"
              size="sm"
              tooltip-position="left"
              @update:model-value="handleToggleFavorite(card)"
              @click.stop />
            <div class="flex flex-col gap-1">
              <div class="flex items-center gap-2">
                <ChartNoAxesCombinedIcon
                  v-if="isTableauDashboardCard(card)"
                  :size="18" />
                <FileIcon
                  v-if="isDocumentCard(card)"
                  :size="18" />
                <LayoutTemplateIcon
                  v-if="isVolatilityModelCard(card)"
                  :size="18" />
                <h2
                  class="text-specialty-teal"
                  data-testid="card-title"
                  :class="{ 'text-sm': card.title.length > 30 }"
                  v-html="filters.highlight(card.title, searchFilter)"></h2>
              </div>
              <div class="flex w-md gap-1 pt-1 text-xs font-medium text-gray-600">
                <template v-if="isTableauDashboardCard(card)">
                  <div><ExternalLinkIcon :size="16" /></div>
                  <div
                    class="overflow-hidden text-nowrap text-ellipsis"
                    v-html="filters.highlight(card.url.replace(/^https?:\/\//, ''), searchFilter)"></div>
                </template>
                <span
                  v-if="isDocumentCard(card)"
                  class="badge badge-accent"
                  v-html="filters.highlight(card.document.documentTypeName, searchFilter)" />
                <span
                  v-else-if="isVolatilityModelCard(card)"
                  class="badge badge-outline badge-secondary">
                  Volatility Model
                </span>
                <span
                  v-else-if="isStockDropModelCard(card)"
                  class="badge badge-outline badge-secondary">
                  Stock Drop Model
                </span>
                <span
                  v-else-if="isCyberModelCard(card)"
                  class="badge badge-outline badge-secondary">
                  Cyber Model
                </span>
              </div>
            </div>

            <table
              class="table-fixed"
              :class="{ 'w-40': isAClientAccount }">
              <tbody>
                <tr v-if="!isAClientAccount && props.selectedCategoryName !== Category.Advisory">
                  <td class="w-24 font-bold">Client:</td>
                  <td v-html="filters.highlight(card.client, searchFilter)"></td>
                </tr>
                <tr v-if="!isAClientAccount">
                  <td class="font-bold">Created by:</td>
                  <td
                    v-if="card.created_by"
                    v-html="filters.highlight(card.created_by, searchFilter)"></td>
                  <td v-else>--</td>
                </tr>
                <tr>
                  <td class="font-bold">Updated:</td>
                  <td>{{ card.updated_on }}</td>
                </tr>
                <tr v-if="isDocumentCard(card)">
                  <td class="font-bold">Tags:</td>
                  <td>
                    <div
                      class="chips m-0 flex flex-wrap gap-1"
                      v-if="card.document.tags && card.document.tags.length > 0">
                      <Badge
                        v-for="tag in card.document.tags"
                        :key="card.document.tags.indexOf(tag)"
                        variant="outline"
                        class="border-gray-300 bg-none font-normal text-primary">
                        <div v-html="filters.highlight(tag, searchFilter)"></div>
                      </Badge>
                    </div>
                    <span v-else>--</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- buttons -->
            <div
              class="flex min-h-6 justify-end gap-1.5"
              v-if="!isClientViewEnabled">
              <button
                v-if="showPin(card)"
                class="group btn w-8 border border-solid border-gray-400 btn-outline btn-xs hover:border-0 hover:bg-primary"
                :class="{ 'border-0 bg-primary': isPinned(card) }"
                :disabled="!hasAnalyticsWritePermission"
                @click.stop="() => handleTogglePin(card)">
                <PinIcon
                  :size="16"
                  class="text-primary group-hover:text-white"
                  :class="{ 'text-white': isPinned(card) }" />
              </button>

              <button
                v-if="showDocumentActions(card) && isDocumentCard(card)"
                class="group btn w-8 border border-solid border-gray-400 btn-outline btn-xs hover:border-0 hover:bg-primary"
                :class="{ 'border-green-500': card.document.published }"
                @click.stop="() => handlePublishDocument(card)">
                <UsersIcon
                  :size="16"
                  class="text-primary group-hover:text-white"
                  :class="{ 'stroke-green-500': card.document.published }" />
              </button>

              <button
                v-if="showTableauActions(card) && isTableauDashboardCard(card)"
                class="group btn w-8 border border-solid border-gray-400 btn-outline btn-xs hover:border-0 hover:bg-primary"
                :class="{ 'border-green-500': card.dashboard.published }"
                @click.stop="() => handlePublishDashboard(card)">
                <UsersIcon
                  :size="16"
                  class="text-primary group-hover:text-white"
                  :class="{ 'stroke-green-500': card.dashboard.published }" />
              </button>

              <button
                v-if="showCyberCubeActions(card) && isCyberModelCard(card)"
                class="group btn w-8 border border-solid border-gray-400 btn-outline btn-xs hover:border-0 hover:bg-primary"
                :class="{ 'border-green-500': card.published }"
                @click.stop="() => handlePublishCyberCubeModel(card)">
                <UsersIcon
                  :size="16"
                  class="text-primary group-hover:text-white"
                  :class="{ 'stroke-green-500': card.published }" />
              </button>

              <button
                v-if="((tableauDomainExists && showTableauActions(card)) || showDocumentActions(card)) && !isAClientAccount"
                class="group btn w-8 border border-solid border-gray-400 btn-outline btn-xs hover:border-0 hover:bg-primary"
                @click.stop="() => openEditModal(card)">
                <EditIcon
                  :size="16"
                  class="text-primary group-hover:text-white" />
              </button>

              <button
                v-if="showModelEdit(card)"
                class="group btn w-8 border border-solid border-gray-400 btn-outline btn-xs hover:border-0 hover:bg-primary"
                @click.stop="() => routeToEdit(card)">
                <EditIcon
                  :size="16"
                  class="text-primary group-hover:text-white" />
              </button>

              <button
                v-if="showDelete(card)"
                class="group btn w-8 border border-solid border-gray-400 btn-outline btn-xs hover:border-0 hover:bg-agency-red"
                @click.stop="() => handleDelete(card)">
                <Trash2Icon
                  class="text-primary group-hover:text-white"
                  :size="16" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>

    <router-view
      v-else
      @updated="emit('updated')" />

    <AppModal
      :show="target.modal.open && target.action !== 'edit'"
      :handle-cancel="() => handleModalCancel()"
      :handle-action="() => handleModalConfirm()"
      :action-button-label="target.modal.actionLabel">
      <template #title>
        <h2 class="pb-1">{{ target.modal.title }}</h2>
      </template>
      <template
        #content
        v-if="target.modal.message">
        <div class="rounded-md bg-amber-50 p-4 text-sm text-amber-900">
          <p class="flex items-center">
            <AlertTriangleIcon
              class="mr-2 text-amber-400"
              :size="24" />
            {{ target.modal.message }}
          </p>
        </div>
      </template>
    </AppModal>

    <DocumentDeleteConfirmModal
      :show="documentDeleteModal.show"
      :document-id="documentDeleteModal.documentId"
      :account-id="null"
      :file-name="documentDeleteModal.fileName"
      @close="handleDocumentDeleteClose"
      @deleted="handleDocumentDeleted" />

    <DocumentEditModal
      v-if="target.modal.open && target.action === 'edit' && target.type === 'document'"
      title="Edit Document"
      ref="editDocumentModal"
      :document="(target.card && isDocumentCard(target.card) && target.card.document) || undefined"
      :policy-document-types="documentTypes"
      :include-tags="true"
      :show="showDocumentEditModal"
      @close="closeDocumentEditModal()"
      @update="(request) => handleModalConfirm(request)" />

    <TableauModal
      :show="target.modal.open && target.action === 'edit' && target.type === 'tableau'"
      mode="edit"
      :dashboard="(target.card && isTableauDashboardCard(target.card) && target.card.dashboard) || undefined"
      :tableauDomain="tableauDomain"
      :tableau-types="tableauTypes"
      :enableAccountSelect="!props.accountId"
      @close="clearTarget()"
      @save="handleUploadDashboard" />

    <AnalyticsUploadModal
      v-model:open="openAnalyticsModal"
      :selected-category-name="selectedCategoryName"
      :analytics-type-id="selectedCategoryType"
      :account-id="props.accountId"
      @submit="handleUploadDashboard" />
  </div>
</template>
